import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import api, { AUTH_TOKEN_UPDATED_EVENT } from 'services/api';

interface User {
  id: number;
  username: string;
  role: string;
  clientId: number | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const normalizeUser = (rawUser: any): User | null => {
  if (!rawUser || typeof rawUser !== 'object') return null;

  const clientIdCandidate = rawUser.clientId ?? rawUser.client_id;
  const role = String(rawUser.role ?? '');
  const isAdmin = role === 'admin';
  const normalizedClientId =
    clientIdCandidate === null || clientIdCandidate === undefined || clientIdCandidate === ''
      ? null
      : Number(clientIdCandidate);

  if (!isAdmin && (!Number.isFinite(normalizedClientId) || Number(normalizedClientId) <= 0)) {
    return null;
  }

  return {
    id: Number(rawUser.id),
    username: String(rawUser.username ?? ''),
    role,
    clientId: normalizedClientId
  };
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? normalizeUser(JSON.parse(savedUser)) : null;
  });
  
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('token');
  });

  useEffect(() => {
    const onTokenRefreshed = (e: Event) => {
      if (!(e instanceof CustomEvent)) return;
      const payload = e.detail;
      if (!payload || typeof payload !== 'object') return;
      const t = Reflect.get(payload, 'token');
      if (typeof t === 'string' && t.length > 0) setToken(t);
    };
    window.addEventListener(AUTH_TOKEN_UPDATED_EVENT, onTokenRefreshed);
    return () => window.removeEventListener(AUTH_TOKEN_UPDATED_EVENT, onTokenRefreshed);
  }, []);

  const login = async (username: string, password: string) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || (
      process.env.NODE_ENV === 'production' 
        ? '/api' 
        : 'http://localhost:5000/api'
    );
    
    const response = await api(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: { username, password }
    });

    if (response.status !== 200) {
      throw new Error('Login failed');
    }

    const data = response.data;
    const normalizedUser = normalizeUser(data.user);
    if (!normalizedUser) {
      throw new Error('Login response missing valid client id');
    }

    setUser(normalizedUser);
    setToken(data.token);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
    localStorage.setItem('token', data.token);
  };

  const logout = async () => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || (
      process.env.NODE_ENV === 'production' 
        ? '/api' 
        : 'http://localhost:5000/api'
    );

    await api.post(`${API_BASE_URL}/auth/logout`);
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  const isAuthenticated = !!user && !!token;
  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
